import { useQuery } from '@apollo/client'
import React from 'react'
import {
  AutocompleteInput,
  LinearProgress,
  required,
  SaveButton,
  SelectInput,
  SimpleForm,
  TextInput,
  Toolbar,
  TranslatableInputs,
  useCreate,
  useCreateSuggestionContext,
  useLocale,
  useResourceContext,
} from 'react-admin'
import { CreateModal } from '~/components'
import { selectionChoicesQuery, selectionIdQuery } from '~/helpers'

const CreateChoice = ({ resource, source, onNewChoice }) => {
  const locale = useLocale()
  const { data, loading } = useQuery(selectionIdQuery({ resource, source }))
  const { filter, onCancel, onCreate } = useCreateSuggestionContext()
  const [create] = useCreate()
  const defaultValues = () => ({ key: filter })

  // react-admin 4 changed useCreate's shape: the mutation takes
  // (resource, { data }, options) rather than a `payload` wrapper, and onSuccess
  // receives the created record itself instead of { data }.
  const handleSubmit = (values) => {
    create(
      'choices',
      {
        data: {
          ...values,
          selection_id: data?.selections?.[0]?.id,
        },
      },
      {
        onSuccess: (record) => {
          const newChoice = {
            ...record,
            id: record.key,
            name: record.translated_name?.[locale] || record.key,
          }
          onCreate(newChoice)
          onNewChoice(newChoice)
        },
      }
    )
  }

  return (
    <CreateModal title='resources.choices.create' open onClose={onCancel} resource='choices'>
      <SimpleForm
        onSubmit={handleSubmit}
        defaultValues={defaultValues}
        toolbar={
          <Toolbar>
            <SaveButton alwaysEnable />
          </Toolbar>
        }
      >
        {loading ? (
          <LinearProgress />
        ) : (
          <>
            <TextInput source='key' label='resources.choices.fields.key' validate={[required()]} />
            <TranslatableInputs locales={['de', 'en']} defaultLocale={locale}>
              <TextInput source={'translated_name'} label='resources.choices.fields.name' />
            </TranslatableInputs>
          </>
        )}
      </SimpleForm>
    </CreateModal>
  )
}

export const SelectionInput = ({ autocomplete = false, ...props }: any) => {
  const locale = useLocale()
  const [newChoices, setNewChoices] = React.useState<any[]>([])
  let resource = useResourceContext()

  let source = props.source
  const splitSourceArray = props.source.split(/\[\d+\]\./)
  if (splitSourceArray.length > 1) {
    resource = splitSourceArray[splitSourceArray.length - 2]
    source = splitSourceArray[splitSourceArray.length - 1]
  }

  const { data, loading } = useQuery(selectionChoicesQuery({ resource, source, locale }))
  const choices = (data ? data.choices : [])
    .map((data) => ({
      id: data.key,
      name: data.translated_name || data.key,
    }))
    .concat(newChoices)

  const onNewChoice = (newChoice) => setNewChoices(newChoices.concat([newChoice]))

  return autocomplete ? (
    <AutocompleteInput
      choices={choices}
      isLoading={loading}
      create={<CreateChoice resource={resource} source={source} onNewChoice={onNewChoice} />}
      createLabel='resources.choices.create'
      createItemLabel='resources.choices.create_item'
      {...props}
    />
  ) : (
    <SelectInput choices={choices} isLoading={loading} {...props} />
  )
}

export default SelectionInput
